import {ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, Output, SimpleChanges} from '@angular/core';
import {ChangeContext, Options} from '@angular-slider/ngx-slider';
import {AttributeDefinitionView} from '../attribute-schema.service';
import {
  AttributeSectionGroup,
  groupFilterableSchemaBySections,
  isFieldVisible,
  isMultiEnumCheckboxUi,
  visibleFilterFields,
  visibleFormFields
} from '../attribute-form-layout.utils';
import {
  buildRangeSliderOptions,
  decodeRangeFilter,
  encodeRangeFilter,
  parseRangeBounds
} from '../attribute-range.utils';
import {decodeMultiEnumFilter, encodeMultiEnumFilter} from '../attribute-multi-enum.utils';

type RangeState = {
  min: number;
  max: number;
  options: Options;
};

@Component({
  standalone: false,
  selector: 'app-attribute-filter',
  templateUrl: './attribute-filter.component.html',
  styleUrls: ['./attribute-filter.component.scss']
})
export class AttributeFilterComponent implements OnChanges {
  @Input() schema: AttributeDefinitionView[] = [];
  @Input() values: Record<string, string | boolean | null> = {};
  /** When set, only the first N filterable fields are shown (quick-filter row). */
  @Input() fieldLimit: number | null = null;
  /** Optional trailing action in the last row slot (e.g. "All filters (7)"). */
  @Input() overflowActionLabel: string | null = null;
  @Output() valuesChange = new EventEmitter<Record<string, string>>();
  @Output() overflowAction = new EventEmitter<void>();

  filterFields: AttributeDefinitionView[] = [];
  sectionGroups: AttributeSectionGroup[] = [];
  groupedView = false;
  localValues: Record<string, string> = {};
  multiEnumValues: Record<string, string[]> = {};
  rangeState: Record<string, RangeState> = {};

  constructor(private changeDetectorRef: ChangeDetectorRef) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['schema'] || changes['values'] || changes['fieldLimit']) {
      this.refreshFilterFields();
      this.syncLocalValues();
      this.syncRangeState();
      this.pruneHiddenFilterValues();
    }
  }

  isFieldVisible(field: AttributeDefinitionView): boolean {
    return isFieldVisible(field, this.valuesForVisibility());
  }

  sectionHasVisibleFields(section: AttributeSectionGroup): boolean {
    return section.fields.some(field => this.isFieldVisible(field));
  }

  isEnum(field: AttributeDefinitionView): boolean {
    return field.dataType === 'ENUM';
  }

  isMultiEnum(field: AttributeDefinitionView): boolean {
    return field.dataType === 'MULTI_ENUM';
  }

  isMultiEnumSelect(field: AttributeDefinitionView): boolean {
    return this.isMultiEnum(field) && !isMultiEnumCheckboxUi(field);
  }

  isMultiEnumCheckbox(field: AttributeDefinitionView): boolean {
    return isMultiEnumCheckboxUi(field);
  }

  multiEnumSelection(field: AttributeDefinitionView): string[] {
    return this.multiEnumValues[field.code] || [];
  }

  isMultiEnumOptionSelected(field: AttributeDefinitionView, key: string): boolean {
    return this.multiEnumSelection(field).includes(key);
  }

  onMultiEnumCheckboxChange(field: AttributeDefinitionView, key: string, checked: boolean): void {
    const current = [...this.multiEnumSelection(field)];
    const next = checked
      ? (current.includes(key) ? current : [...current, key])
      : current.filter(entry => entry !== key);
    this.onMultiEnumChange(field, next);
  }

  onMultiEnumChange(field: AttributeDefinitionView, selected: string[]): void {
    const keys = (selected || []).map(key => `${key}`.trim()).filter(Boolean);
    this.multiEnumValues[field.code] = keys;
    const encoded = encodeMultiEnumFilter(keys);
    if (!encoded) {
      delete this.localValues[field.code];
    } else {
      this.localValues[field.code] = encoded;
    }
    this.pruneHiddenFilterValues();
    this.refreshFilterFieldsIfLimited();
    this.valuesChange.emit({...this.localValues});
    this.changeDetectorRef.markForCheck();
  }

  isBoolean(field: AttributeDefinitionView): boolean {
    return field.dataType === 'BOOLEAN';
  }

  isNumber(field: AttributeDefinitionView): boolean {
    return field.dataType === 'INTEGER' || field.dataType === 'DECIMAL';
  }

  isRange(field: AttributeDefinitionView): boolean {
    return field.dataType === 'RANGE';
  }

  isDate(field: AttributeDefinitionView): boolean {
    return field.dataType === 'DATE';
  }

  isText(field: AttributeDefinitionView): boolean {
    return field.dataType === 'STRING';
  }

  onValueChange(code: string, value: string): void {
    if (!value) {
      delete this.localValues[code];
    } else {
      this.localValues[code] = value;
    }
    this.pruneHiddenFilterValues();
    this.refreshFilterFieldsIfLimited();
    this.valuesChange.emit({...this.localValues});
    this.changeDetectorRef.markForCheck();
  }

  onRangeEnd(field: AttributeDefinitionView, change: ChangeContext): void {
    const bounds = parseRangeBounds(field);
    const encoded = encodeRangeFilter(change.value, change.highValue, bounds);
    if (!encoded) {
      delete this.localValues[field.code];
    } else {
      this.localValues[field.code] = encoded;
    }
    this.rangeState[field.code] = {
      min: change.value,
      max: change.highValue,
      options: this.rangeState[field.code]?.options || buildRangeSliderOptions(bounds),
    };
    this.pruneHiddenFilterValues();
    this.refreshFilterFieldsIfLimited();
    this.valuesChange.emit({...this.localValues});
    this.changeDetectorRef.markForCheck();
  }

  private refreshFilterFieldsIfLimited(): void {
    if (this.fieldLimit != null) {
      this.refreshFilterFields();
    }
  }

  private refreshFilterFields(): void {
    const schema = this.schema || [];
    if (this.fieldLimit == null) {
      this.groupedView = true;
      this.sectionGroups = groupFilterableSchemaBySections(schema);
      this.filterFields = this.sectionGroups.flatMap(section => section.fields);
    } else {
      this.groupedView = false;
      this.sectionGroups = [];
      let fields = visibleFilterFields(schema, this.valuesForVisibility());
      if (this.fieldLimit >= 0) {
        fields = fields.slice(0, this.fieldLimit);
      }
      this.filterFields = fields;
    }
  }

  private valuesForVisibility(): Record<string, unknown> {
    const values: Record<string, unknown> = {...this.localValues};
    for (const [code, selected] of Object.entries(this.multiEnumValues)) {
      values[code] = selected;
    }
    return values;
  }

  private pruneHiddenFilterValues(): void {
    const values = this.valuesForVisibility();
    let changed = false;
    for (const field of visibleFormFields(this.schema || []).filter(row => row.filterable)) {
      if (!isFieldVisible(field, values)) {
        if (this.localValues[field.code] !== undefined) {
          delete this.localValues[field.code];
          changed = true;
        }
        if (this.multiEnumValues[field.code] !== undefined) {
          delete this.multiEnumValues[field.code];
          changed = true;
        }
        if (this.rangeState[field.code] !== undefined) {
          delete this.rangeState[field.code];
          changed = true;
        }
      }
    }
    if (changed) {
      this.refreshFilterFieldsIfLimited();
    }
  }

  private syncLocalValues(): void {
    const next: Record<string, string> = {};
    const multiNext: Record<string, string[]> = {};
    for (const field of this.filterFields) {
      const raw = this.values?.[field.code];
      if (raw === null || raw === undefined || raw === '') {
        continue;
      }
      const asString = String(raw);
      next[field.code] = asString;
      if (this.isMultiEnum(field)) {
        multiNext[field.code] = decodeMultiEnumFilter(asString);
      }
    }
    this.localValues = next;
    this.multiEnumValues = multiNext;
  }

  private syncRangeState(): void {
    const next: Record<string, RangeState> = {};
    for (const field of this.filterFields) {
      if (!this.isRange(field)) {
        continue;
      }
      next[field.code] = this.rangeStateFromFilter(field);
    }
    this.rangeState = next;
  }

  private rangeStateFromFilter(field: AttributeDefinitionView): RangeState {
    const bounds = parseRangeBounds(field);
    const decoded = decodeRangeFilter(this.localValues[field.code]);
    return {
      min: decoded?.min ?? bounds.min,
      max: decoded?.max ?? bounds.max,
      options: buildRangeSliderOptions(bounds),
    };
  }

  private defaultRangeState(field: AttributeDefinitionView): RangeState {
    const bounds = parseRangeBounds(field);
    return {
      min: bounds.min,
      max: bounds.max,
      options: buildRangeSliderOptions(bounds),
    };
  }
}
